import { ChartDetails } from '@DStypes/chart';
import {
  useDeleteChartByIdMutation,
  useGetChartDataFromConfigMutation,
  useLazyGetPreviewChartDataFromConfigQuery,
  useUpdateChartMutation
} from '@api/chart';
import { useUpdateProjectChartOrderMutation } from '@api/projects';
import { CHART_DATA_PROCESSING } from '@constants/messages';
import { setChartData } from '@pages/EditChart/slice';
import {
  updateChartDataById,
  removeChartById,
  removeChartSByIdARR
} from '@pages/ProjectBoard/slice';
import { RootState } from '@store/store';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface ChartRequestType {
  x_axis: string;
  y_axis: string;
  chart_operation: string;
  segment_field: string | null;
  id: string;
  force?: boolean;
  date_group?: string;
  start_date?: string;
  end_date?: string;
  empty_values_included: string;
  percentage_format: string;
  chart_type: string;
  auto_group_numbers: string;
  is_kpi: string;
  collaboration_id?: string;
  filters?: any;
  dynamic_filters?: any;
}

const useCharts = ({
  chartId = '',
  isPreview = false
}: {
  chartId?: string;
  isPreview?: boolean;
}) => {
  const projectBoard = useSelector((state: RootState) => state?.projectBoard);
  const currentChartData = useSelector(
    (state: RootState) => state?.currentChartData
  );
  const dispatch = useDispatch();
  const [isMultipleDeleteStarted, setIsMultipleDeleteStarted] = useState(false);
  const [getChartsData, { isSuccess }] = useGetChartDataFromConfigMutation();
  const [getPreviewChartsData] = useLazyGetPreviewChartDataFromConfigQuery();
  const [deleteChartById] = useDeleteChartByIdMutation();
  const [updateOrderWithBackend] = useUpdateProjectChartOrderMutation();
  const [isLoading, setIsLoading] = useState(false);
  const chartRefetchFunction = useRef(() => {});

  const [updateChart] = useUpdateChartMutation();

  const onUpdateChart = (chartDetails: ChartDetails, chartRating: number) => {
    if (chartDetails?.chart_long_desc)
      chartDetails.chart_long_desc = chartDetails?.chart_long_desc?.trim();
    if (chartDetails?.chart_short_desc)
      chartDetails.chart_short_desc = chartDetails?.chart_short_desc?.trim();
    if (chartDetails?.chart_title)
      chartDetails.chart_title = chartDetails?.chart_title?.trim();

    updateChart({
      id: chartId,
      payload: { ...chartDetails, rating: chartRating }
    });
    if (chartDetails) dispatch(setChartData(chartDetails));
  };

  const getChartData = async (chartRequestParams: ChartRequestType) => {
    const {
      x_axis,
      y_axis,
      chart_operation,
      segment_field,
      id = chartId,
      force = false,
      start_date,
      end_date,
      empty_values_included = 'False',
      percentage_format = 'False',
      auto_group_numbers = 'True',
      is_kpi = 'False',
      date_group = '',
      chart_type,
      collaboration_id = '',
      filters = {},
      dynamic_filters = {}
    } = chartRequestParams;

    const fetchData: any = async () => {
      try {
        const activeFilters = Object.keys(filters).reduce(
          (acc: any, key: any) => {
            if (filters[key].active === 'true') acc[key] = filters[key];

            return acc;
          },
          {}
        );

        const activeDynamicFilters = Object.keys(dynamic_filters).reduce(
          (acc: any, key: any) => {
            if (dynamic_filters[key].active === 'true')
              acc[key] = dynamic_filters[key];

            return acc;
          },
          {}
        );

        const params = {
          x_axis,
          y_axis,
          chart_operation,
          segment_field,
          id,
          date_group,
          ...(start_date && end_date && { start_date, end_date }),
          empty_values_included,
          percentage_format,
          is_kpi,
          auto_group_numbers,
          chart_type,
          collaboration_id,
          filters,
          dynamic_filters
        };

        if (isPreview) {
          const chartData = await getPreviewChartsData({
            id: id,
            collaboration_id: collaboration_id,
            payload: {
              x_axis: x_axis,
              y_axis: y_axis,
              operation: chart_operation,
              empty_values_included: empty_values_included,
              auto_group_numbers: auto_group_numbers,
              chart_type: chart_type,
              is_kpi: is_kpi,
              ...(segment_field && { segment_field: segment_field }),
              ...(date_group && { date_group: date_group }),
              ...(start_date && { start_date: start_date }),
              ...(end_date && { end_date: end_date }),
              filters: activeFilters,
              dynamic_filters: activeDynamicFilters,
              percentage_format: percentage_format
            }
          }).unwrap();

          dispatch(updateChartDataById({ id, data: chartData }));

          return chartData;
        } else if (!projectBoard?.data?.[id] || force) {
          const chartData = await getChartsData({
            id: id,
            collaboration_id: collaboration_id,
            payload: {
              x_axis: currentChartData?.config?.x_axis?.id || params?.x_axis,
              y_axis: currentChartData?.config?.y_axis?.id || params?.y_axis,
              operation: chart_operation,
              empty_values_included: empty_values_included,
              percentage_format: percentage_format,
              auto_group_numbers: auto_group_numbers,
              chart_type: chart_type,
              is_kpi: is_kpi,
              ...(segment_field && { segment_field: segment_field }),
              ...(date_group && { date_group: date_group }),
              ...(start_date && { start_date: start_date }),
              ...(end_date && { end_date: end_date }),
              filters: activeFilters,
              dynamic_filters: activeDynamicFilters
            }
          }).unwrap();

          if (chartData.error === CHART_DATA_PROCESSING) {
            await new Promise((resolve) => setTimeout(resolve, 800));

            return fetchData(); // Retry fetching data
          } else {
            dispatch(updateChartDataById({ id, data: chartData }));
            setIsLoading(false);

            return chartData;
          }
        }
      } catch (err) {
        console.log(err);
        throw err;
      }
    };

    setIsLoading(true);

    return fetchData();
  };

  // const getChartData = async (chartRequestParams: ChartRequestType) => {
  //   const {
  //     x_axis,
  //     y_axis,
  //     chart_operation,
  //     segment_field,
  //     id = chartId,
  //     force = false,
  //     start_date,
  //     end_date,
  //     empty_values_included = 'False',
  //     auto_group_numbers = 'True',
  //     is_kpi = 'False',
  //     date_group = '',
  //     chart_type,
  //     collaboration_id = '',
  //     filters = {},
  //     dynamic_filters = {}
  //   } = chartRequestParams;

  //   try {
  //     const activeFilters = Object.keys(filters).reduce(
  //       (acc: any, key: any) => {
  //         if (filters[key].active === 'true') acc[key] = filters[key];

  //         return acc;
  //       },
  //       {}
  //     );

  //     const activeDynamicFilters = Object.keys(dynamic_filters).reduce(
  //       (acc: any, key: any) => {
  //         if (dynamic_filters[key].active === 'true')
  //           acc[key] = dynamic_filters[key];

  //         return acc;
  //       },
  //       {}
  //     );

  //     if (isPreview) {
  //       const chartData = await getPreviewChartsData({
  //         id: id,
  //         collaboration_id: collaboration_id,
  //         payload: {
  //           x_axis: x_axis,
  //           y_axis: y_axis,
  //           operation: chart_operation,
  //           empty_values_included: empty_values_included,
  //           auto_group_numbers: auto_group_numbers,
  //           chart_type: chart_type,
  //           is_kpi: is_kpi,
  //           ...(segment_field && { segment_field: segment_field }),
  //           ...(date_group && { date_group: date_group }),
  //           ...(start_date && { start_date: start_date }),
  //           ...(end_date && { end_date: end_date }),
  //           filters: activeFilters,
  //           dynamic_filters: activeDynamicFilters
  //         }
  //       }).unwrap();

  //       dispatch(updateChartDataById({ id, data: chartData }));
  //     } else if (!projectBoard?.data?.[id] || force) {
  //       const params = {
  //         x_axis,
  //         y_axis,
  //         chart_operation,
  //         segment_field,
  //         id,
  //         date_group,
  //         // : date_group === 'none' ? DATE_GROUP.DAYS : date_group,
  //         ...(start_date && end_date && { start_date, end_date }),
  //         empty_values_included,
  //         is_kpi,
  //         auto_group_numbers,
  //         chart_type,
  //         collaboration_id,
  //         filters,
  //         dynamic_filters
  //       };

  //       // Function to refetch chart data if data is in processing state
  //       chartRefetchFunction.current = async () => {
  //         const chartData = await getChartsData({
  //           id: id,
  //           collaboration_id: collaboration_id,
  //           payload: {
  //             x_axis: currentChartData?.config?.x_axis?.id || params?.x_axis,
  //             y_axis: currentChartData?.config?.y_axis?.id || params?.y_axis,
  //             operation: chart_operation,
  //             empty_values_included: empty_values_included,
  //             auto_group_numbers: auto_group_numbers,
  //             chart_type: chart_type,
  //             is_kpi: is_kpi,
  //             ...(segment_field && { segment_field: segment_field }),
  //             ...(date_group && { date_group: date_group }),
  //             ...(start_date && { start_date: start_date }),
  //             ...(end_date && { end_date: end_date }),
  //             filters: activeFilters,
  //             dynamic_filters: activeDynamicFilters
  //           }
  //         }).unwrap();

  //         if (chartData.error === CHART_DATA_PROCESSING) {
  //           setTimeout(async () => chartRefetchFunction.current(), 800);
  //         } else {
  //           // console.log({ id: id, data: chartData });
  //           dispatch(updateChartDataById({ id, data: chartData }));
  //           setIsLoading(false);
  //         }
  //       };
  //       setIsLoading(true);
  //       chartRefetchFunction.current();
  //     }
  //   } catch (err) {
  //     console.log(err);
  //   }
  // };

  const deleteChart = async (id = chartId, isSingle = true) => {
    try {
      await deleteChartById(id).unwrap();
      if (isSingle) {
        await updateOrderWithBackend({
          id: projectBoard.currentProjectData?.id,
          payload: projectBoard.currentProjectData?.chart_id_order.filter(
            (item) => item !== id
          )
        });
        dispatch(removeChartById({ id }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const deleteMultipleCharts = async (chartIds: string[]) => {
    setIsMultipleDeleteStarted(true);
    await updateOrderWithBackend({
      id: projectBoard.currentProjectData?.id,
      payload: projectBoard.currentProjectData?.chart_id_order.filter(
        (item) => !chartIds.includes(item)
      )
    });
    dispatch(removeChartSByIdARR({ idArr: chartIds }));

    await Promise.all(chartIds.map((chartId) => deleteChart(chartId, false)));
    setIsMultipleDeleteStarted(false);
  };

  return {
    getChartData,
    configs: projectBoard?.configs,
    isSuccess,
    deleteChart,
    deleteMultipleCharts,
    isMultipleDeleteStarted,
    isLoading,
    chartRefetchFunction: chartRefetchFunction.current,
    onUpdateChart
  };
};

export default useCharts;
